import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import Input from "@/components/ui/Input";
import Textarea from "@/components/ui/Textarea";
import { Alert } from "@/components/ui";
import {
  SUPPORTED_LOCALES,
  localeFlag,
  supportedLanguageLabel,
  type SupportedLocale,
} from "@/lib/locales";
import { cn } from "@/lib/utils";
import type { TranslationFormValues } from "@/lib/translationResolution";

export interface CatalogTranslatedFieldProps {
  values: TranslationFormValues;
  onChange: (values: TranslationFormValues) => void;
  label: string;
  fieldAriaLabel: (languageLabel: string) => string;
  selectedLanguage: SupportedLocale;
  onSelectedLanguageChange: (language: SupportedLocale) => void;
  multiline?: boolean;
  maxLength?: number;
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** When false, empty values are allowed (item descriptions may be omitted entirely). */
  requireAllLocales?: boolean;
  banner?: ReactNode;
  inputClassName?: string;
  rows?: number;
  languageControlsPlacement?: "above" | "below";
  /** Hide the input/textarea label (use fieldAriaLabel instead). */
  hideLabel?: boolean;
  /** Hide the large field heading above the language chips. */
  hideHeading?: boolean;
  /** Hide chips when another field owns the shared language switcher. */
  hideLanguageControls?: boolean;
  /** Override chip completeness (e.g. title + description must both be filled). */
  isLocaleComplete?: (code: SupportedLocale) => boolean;
}

export function CatalogTranslatedField({
  values,
  onChange,
  label,
  fieldAriaLabel,
  selectedLanguage,
  onSelectedLanguageChange,
  multiline = false,
  maxLength,
  placeholder,
  hint,
  error,
  required = false,
  requireAllLocales = true,
  banner,
  inputClassName,
  rows = 10,
  languageControlsPlacement = "above",
  hideLabel = false,
  hideHeading = false,
  hideLanguageControls = false,
  isLocaleComplete,
}: CatalogTranslatedFieldProps) {
  const { t: tComponents } = useTranslation("components");
  const fieldId = useId();

  const handleTextChange = (value: string) => {
    onChange({
      ...values,
      [selectedLanguage]: value,
    });
  };

  const selectedValue = values[selectedLanguage] || "";
  const languageLabel = supportedLanguageLabel(selectedLanguage);
  const fieldLabel = hideLabel
    ? undefined
    : `${label} (${languageLabel})`;
  const localeRequired = required && requireAllLocales;
  const hasOverLimit =
    maxLength !== undefined &&
    SUPPORTED_LOCALES.some((code) => (values[code]?.length ?? 0) > maxLength);

  const languageControls = hideLanguageControls ? null : (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        languageControlsPlacement === "above" ? "mb-3" : "mt-3",
      )}
    >
      {SUPPORTED_LOCALES.map((code) => {
        const flag = localeFlag(code);
        const isSelected = code === selectedLanguage;
        const isComplete = isLocaleComplete
          ? isLocaleComplete(code)
          : Boolean(values[code]?.trim());
        const isOverLimit =
          maxLength !== undefined && (values[code]?.length ?? 0) > maxLength;

        return (
          <button
            key={code}
            type="button"
            onClick={() => onSelectedLanguageChange(code)}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-medium transition-colors",
              isSelected &&
                !isComplete &&
                "border-primary-500 bg-primary-50 text-primary-800 ring-[3px] ring-primary-500/15",
              isSelected &&
                isOverLimit &&
                "border-error-500 bg-error-50 text-error-800 ring-[3px] ring-error-500/15",
              isSelected &&
                isComplete &&
                !isOverLimit &&
                "border-success-500 bg-success-50 text-success-800 ring-[3px] ring-success-500/15",
              !isSelected &&
                isOverLimit &&
                "border-error-200 bg-error-50 text-error-700 hover:bg-error-100",
              !isSelected &&
                isComplete &&
                !isOverLimit &&
                "border-success-200 bg-success-50/60 text-success-700 hover:bg-success-50",
              !isSelected &&
                !isComplete &&
                "border-surface-200 bg-surface-0 text-surface-600 hover:bg-surface-50 hover:text-surface-900 dark:bg-surface-50/60",
            )}
            aria-pressed={isSelected}
            aria-label={fieldAriaLabel(supportedLanguageLabel(code))}
          >
            <span aria-hidden>{flag}</span>
            <span>{supportedLanguageLabel(code)}</span>
            {localeRequired && !isComplete && (
              <span className="text-xs font-normal text-error-600">*</span>
            )}
            {isComplete && (
              <Check
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  isOverLimit ? "text-error-600" : "text-success-600",
                )}
                aria-hidden
              />
            )}
          </button>
        );
      })}
    </div>
  );

  const textField = multiline ? (
    <Textarea
      id={`${fieldId}-${selectedLanguage}`}
      label={fieldLabel}
      value={selectedValue}
      onChange={(e) => handleTextChange(e.target.value)}
      error={error}
      hint={error ? undefined : hint}
      maxLength={maxLength}
      required={localeRequired}
      rows={rows}
      placeholder={placeholder}
      className={cn("min-h-[12.5rem]", inputClassName)}
      aria-label={
        hideLabel
          ? fieldAriaLabel(supportedLanguageLabel(selectedLanguage))
          : undefined
      }
    />
  ) : (
    <Input
      id={`${fieldId}-${selectedLanguage}`}
      label={fieldLabel}
      value={selectedValue}
      onChange={(e) => handleTextChange(e.target.value)}
      error={error}
      hint={error ? undefined : hint}
      maxLength={maxLength}
      required={localeRequired}
      placeholder={placeholder}
      className={inputClassName}
      aria-label={
        hideLabel
          ? fieldAriaLabel(supportedLanguageLabel(selectedLanguage))
          : undefined
      }
    />
  );

  return (
    <div>
      {!hideLabel && !hideHeading && label ? (
        <h3 className="mb-3 text-sm font-semibold text-surface-900">{label}</h3>
      ) : null}

      {banner}

      {languageControlsPlacement === "above" ? languageControls : null}
      {hasOverLimit && maxLength !== undefined ? (
        <Alert variant="error" className="mb-3">
          {tComponents("translationOverLimit", { max: maxLength })}
        </Alert>
      ) : null}
      {textField}
      {languageControlsPlacement === "below" ? languageControls : null}
      {maxLength !== undefined ? (
        <p
          className={cn(
            "mt-1 text-right text-xs tabular-nums",
            selectedValue.length >= maxLength
              ? "font-medium text-error-600"
              : "text-surface-500",
          )}
          aria-live="polite"
        >
          {tComponents("translationCharCount", {
            current: selectedValue.length,
            max: maxLength,
          })}
        </p>
      ) : null}
    </div>
  );
}
